import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Input, Label } from "../ui/input";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../ui/tooltip";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Sparkles, Check, Rocket, Info } from "lucide-react";
import { cn, apiErr } from "../../lib/utils";
import {
  PROVISION_WIZARD_STEPS, DATA_SOURCE_OPTIONS, OWNER_ROLES, BASELINE_FIELDS,
  BASELINE_YEAR_TIP, MONTHS, PERIOD_TIPS,
} from "../../lib/onboardingConfig";

const CADENCES = [
  { v: "monthly", l: "Monthly" },
  { v: "quarterly", l: "Quarterly" },
  { v: "annual", l: "Annual" },
];

// Phase-2 provisioning wizard. Account already exists; completing it provisions the
// tenant (Lestari Lite trial) for the logged-in user.
export function WizardStage({ sessionId, session, onActivated }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const cfg = PROVISION_WIZARD_STEPS[step];

  const [period, setPeriod] = useState({
    fy_end_month: session?.profile?.fy_end_month || 12, base_year: 2024, cadence: "monthly",
  });
  const [owners, setOwners] = useState(OWNER_ROLES.map((o) => ({ ...o, name: "", email: "" })));
  const [sources, setSources] = useState(["manual"]);
  const [baseline, setBaseline] = useState({ baseline_year: 2024 });
  const [topics, setTopics] = useState([]);
  const [accepted, setAccepted] = useState({});

  useEffect(() => {
    if (cfg.key === "materiality" && topics.length === 0) {
      api.post("/onboarding/recommend-materiality", { session_id: sessionId }).then((r) => {
        const t = r.data.topics || [];
        setTopics(t);
        const acc = {};
        t.forEach((topic, i) => { acc[i] = topic.priority !== "low"; });
        setAccepted(acc);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    api.get(`/onboarding/session/${sessionId}`).then(({ data: s }) => {
      if (cancelled || !s) return;
      const w = s.wizard || {};
      if (w.period) setPeriod((p) => ({ ...p, ...w.period }));
      if (Array.isArray(w.sources?.sources) && w.sources.sources.length) setSources(w.sources.sources);
      if (Array.isArray(w.owners?.owners) && w.owners.owners.length) {
        setOwners((prev) => prev.map((o) => {
          const m = w.owners.owners.find((x) => x.area === o.area);
          return m ? { ...o, name: m.name || "", email: m.email || "" } : o;
        }));
      }
      if (s.baseline && Object.keys(s.baseline).length) setBaseline((b) => ({ ...b, ...s.baseline }));
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const toggle = (arr, setArr, val) => setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const invitedUsers = useMemo(
    () => owners.filter((o) => o.email.trim()).map((o) => ({ name: o.name, email: o.email.trim(), role: o.role })),
    [owners]
  );

  const persistAndNext = async () => {
    setBusy(true);
    try {
      let data = {};
      if (cfg.key === "period") data = { ...period, fy_end_month: parseInt(period.fy_end_month), base_year: parseInt(period.base_year) };
      if (cfg.key === "owners") data = { owners: owners.filter((o) => o.name || o.email), invited_users: invitedUsers };
      if (cfg.key === "sources") data = { sources };
      if (cfg.key === "baseline") data = baseline;
      if (cfg.key === "materiality") {
        data = { accepted: topics.filter((_, i) => accepted[i]), deferred: topics.filter((_, i) => !accepted[i]) };
      }
      await api.put(`/onboarding/wizard/${cfg.key}`, { session_id: sessionId, data });
      setStep((s) => s + 1);
    } catch (e) {
      toast.error(apiErr(e, "Could not save step"));
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/provision/activate", { session_id: sessionId });
      onActivated(data);
    } catch (e) {
      toast.error(apiErr(e, "Activation failed"));
    } finally {
      setBusy(false);
    }
  };

  const org = session?.organisation || {};
  const profile = session?.profile || {};

  return (
    <div className="max-w-2xl" data-testid="wizard-stage">
      <div className="flex items-center gap-1.5 mb-5">
        {PROVISION_WIZARD_STEPS.map((s, i) => (
          <div key={s.key} className={cn("h-1 flex-1 rounded-full transition-colors", i <= step ? "bg-accent" : "bg-border")} />
        ))}
      </div>
      <div className="label-eyebrow">Provisioning wizard · Step {step + 1} / {PROVISION_WIZARD_STEPS.length}</div>
      <h2 className="font-display text-3xl font-bold tracking-tight mt-1">{cfg.title}</h2>

      <div className="card-flat bg-accent/10 border-accent/30 p-4 mt-4 flex gap-3" data-testid="wizard-ai-explain">
        <Sparkles className="h-4 w-4 text-accent shrink-0 mt-0.5" />
        <div className="text-sm leading-relaxed">{cfg.ai}</div>
      </div>

      <div className="card-flat bg-surface p-5 mt-5">
        {cfg.key === "period" && (
          <TooltipProvider delayDuration={150}>
            <div className="grid sm:grid-cols-3 gap-4" data-testid="wizard-period">
              <div>
                <LabelWithTip label="Financial year-end" tip={PERIOD_TIPS.fy_end_month} testid="tip-fy_end_month" />
                <Select value={String(period.fy_end_month)} onValueChange={(v) => setPeriod({ ...period, fy_end_month: v })}>
                  <SelectTrigger data-testid="period-fy"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <LabelWithTip label="Base / first reporting year" tip={PERIOD_TIPS.base_year} testid="tip-base_year" />
                <Input type="number" value={period.base_year} onChange={(e) => setPeriod({ ...period, base_year: e.target.value })} data-testid="period-year" />
              </div>
              <div>
                <LabelWithTip label="Data collection cadence" tip={PERIOD_TIPS.cadence} testid="tip-cadence" />
                <Select value={period.cadence} onValueChange={(v) => setPeriod({ ...period, cadence: v })}>
                  <SelectTrigger data-testid="period-cadence"><SelectValue /></SelectTrigger>
                  <SelectContent>{CADENCES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </TooltipProvider>
        )}

        {cfg.key === "owners" && (
          <div className="space-y-2.5" data-testid="wizard-owners">
            <p className="text-xs text-muted-foreground">Add an email to invite a colleague. Leave blank to skip.</p>
            {owners.map((o, i) => (
              <div key={o.area} className="grid grid-cols-1 sm:grid-cols-[1.1fr_1fr_1.2fr] gap-2 items-center">
                <div className="text-sm font-medium">{o.area}</div>
                <Input placeholder="Name" value={o.name} onChange={(e) => setOwners(owners.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} data-testid={`owner-name-${i}`} />
                <Input placeholder="email@company.sg" value={o.email} onChange={(e) => setOwners(owners.map((x, j) => j === i ? { ...x, email: e.target.value } : x))} data-testid={`owner-email-${i}`} />
              </div>
            ))}
            {invitedUsers.length > 0 && <div className="text-xs text-accent font-mono">{invitedUsers.length} colleague(s) will be invited.</div>}
          </div>
        )}

        {cfg.key === "sources" && (
          <TooltipProvider delayDuration={150}>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="wizard-sources">
              {DATA_SOURCE_OPTIONS.map((s) => (
                <Chip key={s.value} active={sources.includes(s.value)} onClick={() => toggle(sources, setSources, s.value)} testid={`source-${s.value}`} tip={s.tip} tipTestid={`source-tip-${s.value}`}>
                  {s.label}
                </Chip>
              ))}
            </div>
          </TooltipProvider>
        )}

        {cfg.key === "baseline" && (
          <div data-testid="wizard-baseline">
            <div className="card-flat bg-accent/5 border-accent/20 p-3 mb-4 text-xs text-muted-foreground leading-relaxed">
              No need for perfect numbers — a sensible estimate today beats a blank box. Hover the
              <Info className="inline h-3 w-3 mx-0.5 text-accent align-text-bottom" /> on any field for a quick tip. You can refine everything later.
            </div>
            <TooltipProvider delayDuration={150}>
              <div className="grid sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <LabelWithTip label="Baseline year" tip={BASELINE_YEAR_TIP} testid="tip-baseline_year" />
                  <Input type="text" inputMode="numeric" value={baseline.baseline_year} onChange={(e) => setBaseline({ ...baseline, baseline_year: e.target.value })} data-testid="baseline-year" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {BASELINE_FIELDS.map((f) => (
                  <div key={f.key}>
                    <LabelWithTip label={f.label} tip={f.tip} testid={`tip-${f.key}`} />
                    <Input type="text" inputMode="decimal" value={baseline[f.key] || ""} onChange={(e) => setBaseline({ ...baseline, [f.key]: e.target.value })} placeholder="Optional" data-testid={`baseline-${f.key}`} />
                  </div>
                ))}
              </div>
            </TooltipProvider>
            <p className="text-xs text-muted-foreground mt-3">Enter whatever you have — you can complete the rest later in the platform.</p>
          </div>
        )}

        {cfg.key === "materiality" && (
          <div className="space-y-2.5" data-testid="wizard-materiality">
            {topics.length === 0 && <div className="text-sm text-muted-foreground font-mono">Generating recommendations…</div>}
            {topics.map((t, i) => (
              <div key={i} className={cn("border rounded-sm p-3 flex items-start gap-3", accepted[i] ? "border-accent/40 bg-accent/5" : "border-border")} data-testid={`topic-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{t.topic}</span>
                    <Badge variant="outline">{t.dimension}</Badge>
                    {t.priority === "high" && <Badge variant="warning">HIGH</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{t.why}</div>
                  <div className="text-[11px] text-muted-foreground mt-1 font-mono">KPIs: {(t.kpis || []).join(", ")} · {t.framework}</div>
                </div>
                <Button variant={accepted[i] ? "primary" : "outline"} size="sm" onClick={() => setAccepted({ ...accepted, [i]: !accepted[i] })} data-testid={`topic-toggle-${i}`}>
                  {accepted[i] ? <><Check className="h-3.5 w-3.5" /> Accepted</> : "Defer"}
                </Button>
              </div>
            ))}
            {topics.length > 0 && (
              <div className="text-xs text-muted-foreground font-mono pt-1">
                {Object.values(accepted).filter(Boolean).length} accepted · {topics.length - Object.values(accepted).filter(Boolean).length} deferred
              </div>
            )}
          </div>
        )}

        {cfg.key === "review" && (
          <div className="space-y-4" data-testid="wizard-review">
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Row label="Organisation" value={org.entity_name} />
              <Row label="UEN" value={org.uen} />
              <Row label="Entity category" value={profile.entity_category} />
              <Row label="Regulatory tier" value={session?.regulatory_tier} />
              <Row label="Industry" value={profile.sector} />
              <Row label="Financial year-end" value={MONTHS.find((m) => m.value === String(period.fy_end_month))?.label} />
              <Row label="Base year" value={period.base_year} />
              <Row label="Data owners" value={`${owners.filter((o) => o.name || o.email).length} assigned`} />
              <Row label="Trial" value="15 days · Lestari Lite" />
            </div>
            <div className="card-flat bg-accent/10 border-accent/30 p-3 text-xs leading-relaxed" data-testid="wizard-activate-note">
              Activating provisions your secure Lestari workspace and starts your 15-day Lestari Lite trial. You'll land
              on your onboarding dashboard with guided next steps.
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-6">
        <Button variant="outline" disabled={busy || step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="wizard-back-btn">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        {cfg.key !== "review" ? (
          <Button variant="primary" size="lg" className="gap-2" disabled={busy} onClick={persistAndNext} data-testid="wizard-next-btn">
            {busy ? "Saving…" : "Save & continue"} <ArrowRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button variant="primary" size="lg" className="gap-2" disabled={busy} onClick={activate} data-testid="wizard-activate-btn">
            <Rocket className="h-4 w-4" /> {busy ? "Activating…" : "Activate & enter Lestari"}
          </Button>
        )}
      </div>
    </div>
  );
}

function Chip({ active, onClick, children, testid, tip, tipTestid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={cn("flex items-center gap-2 px-3 py-2 rounded-sm border text-sm text-left transition-colors relative",
        active ? "border-accent bg-accent/10 text-foreground" : "border-border hover:bg-surface-hover text-muted-foreground")}>
      <span className={cn("h-4 w-4 rounded-sm border flex items-center justify-center shrink-0", active ? "bg-accent border-accent" : "border-border")}>
        {active && <Check className="h-3 w-3 text-black" />}
      </span>
      <span className="flex-1">{children}</span>
      {tip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span role="button" tabIndex={0} aria-label="More info" data-testid={tipTestid} onClick={(e) => e.stopPropagation()}
              className="text-muted-foreground hover:text-accent focus-visible:text-accent focus:outline-none transition-colors shrink-0 cursor-help">
              <Info className="h-3.5 w-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">{tip}</TooltipContent>
        </Tooltip>
      )}
    </button>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/50 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono text-foreground text-right">{value || "—"}</span>
    </div>
  );
}

function LabelWithTip({ label, tip, testid }) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <Label className="mb-0">{label}</Label>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label={`Tip: ${label}`} data-testid={testid}
            className="text-muted-foreground hover:text-accent focus-visible:text-accent focus:outline-none transition-colors -mt-0.5">
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">{tip}</TooltipContent>
      </Tooltip>
    </div>
  );
}
